import axios from "axios";
import { PublicConfig } from "@/shared/PublicConfig";
import { TrustPilotRawReview, TrustPilotReview, TrustPilotSummary, MOCK_TRUST_PILOT_DATA, TrustPilotData } from "./TrustPilotTypes";

const API_BASE_URL = "https://api.trustpilot.com/v1";
const DEFAULT_REVIEW_COUNT = 10;

export class TrustPilotUtils {
    static async getData(underwriter: string, tag?: string): Promise<TrustPilotData> {
        const config = PublicConfig.TRUSTPILOT_CONFIG[underwriter];

        if (!config) {
            throw new Error(`No TrustPilot configuration found for underwriter: ${underwriter}`);
        }

        const { BUSINESS_UNIT_ID, API_KEY } = config;

        try {
            const reviewCount = Number(DEFAULT_REVIEW_COUNT);
            const [reviews, summary] = await Promise.all([getReviews(BUSINESS_UNIT_ID, API_KEY, reviewCount, tag), getBusinessSummary(BUSINESS_UNIT_ID, API_KEY)]);

            return {
                reviews: reviews.filter(review => review.isVerified),
                summary
            };
        } catch (e) {
            // Return mock data if there's an issue with the API
            return MOCK_TRUST_PILOT_DATA;
        }
    }
}

// Fetch reviews and business summary dynamically based on the underwriter configuration
async function getReviews(businessUnitId: string, apiKey: string, reviewCount: number, tag?: string): Promise<TrustPilotReview[]> {
    const tagParams = tag ? `&tagValue=${encodeURIComponent(tag)}` : "";
    const {
        data: { reviews: rawReviewList }
    } = await axios.get(`${API_BASE_URL}/business-units/${businessUnitId}/reviews?stars=5&perPage=${reviewCount}&apikey=${apiKey}${tagParams}`);

    return rawReviewList.map((rawReview: TrustPilotRawReview) => ({
        id: rawReview.id,
        title: rawReview.title,
        review: rawReview.text,
        stars: rawReview.stars,
        name: rawReview.consumer.displayName,
        createdAt: rawReview.createdAt,
        isVerified: rawReview.isVerified
    }));
}

async function getBusinessSummary(businessUnitId: string, apiKey: string): Promise<TrustPilotSummary> {
    const { data } = await axios.get(`${API_BASE_URL}/business-units/${businessUnitId}?apikey=${apiKey}`);

    return {
        displayName: data.displayName,
        totalReviews: data.numberOfReviews.total,
        // The business-unit response already breaks reviews down by rating, so the 5-star count costs
        // no extra request. `total` counts every rating and overstates any "5-star reviews" claim.
        fiveStarReviews: data.numberOfReviews.fiveStars,
        score: data.score.trustScore
    };
}
